import { Grid, IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { ReactComponent as DeleteIcon } from "assets/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "assets/EditIcon.svg";
import { ReactComponent as TimeClockCircle } from "assets/TimeClockCircle.svg";
import Backbutton from "assets/backbutton";
import { ReactComponent as CopyLink } from "assets/copy_link.svg";
import FileAdd from "assets/fileAdd";
import { postUserVisit } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import CloseButton from "components/common/CloseButton";
import Feedback from "components/common/Feedback";
import Input from "components/common/Input";
import KebabMenu from "components/common/KebabMenu";
import ButtonLink from "components/common/Link";
import RichText from "components/common/RichText";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Timing from "components/common/Timing";
import Typography from "components/common/Typography";
import UrlTabs from "components/common/UrlTabs";
import MyPlanActionDetail from "components/features/ActionCardDetails/MyPlanActionDetail";
import { copyActionAsync, updateMyPlanCardDetailAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import Resources from "components/features/Resources";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty } from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROPERTY, accessTypes, brandDeadLine, FEEDBACK_TIMER, messageTypes } from "utils/CommonConstants";
import { getHoursAndMin, getOwnersList, getUserHltId, matchOwnerWithHltId, matchPlanType } from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import { checkTemplatePlanPayload } from "utils/StoreUtils";
import { planTypes, responseTypes } from "utils/Types";
import { REDIRECT_URI } from "utils/URLConstants";
import { commonApiCallParam, isFunctionalArea, isLibraryPage } from "utils/UserAnalyticsUtils";
import Button from "../../common/Button";
import ActionCardDialogsContext from "../ActionCard/Context";
import Index from "../CardDialog";
import { getTabDetails } from "../CardDialog/TabsConfig";
import { checkCopyActionAccess } from "../CopyToPlan/Helper";
import UpdateStatusConfirmDialog from "../planDetails/UpdateStatusConfirmationDialog";
import ActionButtons from "./ActionButtons";
import "./index.scss";

export default function ActionDetailsDialog(props: any) {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const activeCard = useAppSelector((state: any) => state.app.activeActionCard);
  const selectedMyPlanDetail: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const isRedirectFromNotification: any = useAppSelector((state) => state.myPlanDetails.isRedirectFromNotification);
  const { isEdit, open, handleClose, assigneeList, isNew, isPreview = false, setShowPreview } = props;
  const restrictPlanType = useAppSelector((state) => state.myPlanDetails.myPlanDetails.planType);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const libraryPlan = useAppSelector((state: any) => state.planDetails.planDetails);
  const isTemplatePlan = selectedMyPlanDetail.innCode === ABOVE_PROPERTY;
  const location = useLocation();
  const titleRef: any = useRef();

  let {
    planId,
    planType = isNew ? planTypes.MyPlans : planTypes.AllPlans,
    sectionId,
    cardId,
    name,
    timeEstimateMinutes,
    id,
    linkToResources,
    body: cardBody
  } = activeCard || {};

  useEffect(() => {
    const selectedPlan = !isEmpty(selectedMyPlanDetail) ? selectedMyPlanDetail : libraryPlan;
    if (open && (isFunctionalArea() || !isEmpty(selectedPlan))) {
      const param = commonApiCallParam();
      if (isLibraryPage()) {
        param.cmsCardId = activeCard.id;
        param.cardTitle = activeCard.name;
        param.planName = selectedPlan.name;
        param.cmsPlanId = selectedPlan.id;
      } else if (!isFunctionalArea()) {
        param.planName = selectedPlan.name;
        param.cmsPlanId = selectedPlan.cmsPlanId;
        param.planId = selectedPlan.id;
        param.cmsCardId = activeCard.cmsCardId;
        param.cardId = activeCard.cardId;
        param.cardTitle = activeCard.name;
      } else {
        param.cmsCardId = activeCard.id;
        param.cardTitle = activeCard.name;
      }
      param.cardVisit = true;
      dispatch(postUserVisit(param));
    }
  }, [open, selectedMyPlanDetail, libraryPlan]);

  const methods = useForm({
    defaultValues: {
      name,
      timeEstimateMinutes,
      linkToResources: linkToResources || [{ title: "", url: "" }]
    }
  });

  const {
    register,
    formState: { errors }
  } = methods;

  const [body, setBody] = useState(cardBody);
  const [assigneeVal, setAssigneeVal] = useState();
  const [isCopyLinkSuccess, setIsCopyLinkSuccess] = useState(false);
  const { isMobileScreen } = useContext(ThemeContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [updatePayload, setUpdatePayload] = useState<any>();
  const {
    setIsOpenEditActionDialog,
    setIsOpenCopyActionDialog,
    setIsOpenViewActionDialog,
    setIsOpenDeleteActionDialog,
    setIsOpenPlanDetaiLDialog
  } = useContext(ActionCardDialogsContext);

  const getMenuOptions = () => {
    return planType === planTypes.MyPlans
      ? [
          {
            label: "Edit Action",
            icon: EditIcon,
            accessType: accessTypes.update,
            enabled: true,
            action: () => {
              if (matchPlanType(restrictPlanType) && activeCard?.cardEditable === "N") {
                dispatch(
                  setFeedbackDetails({
                    shouldShow: true,
                    type: "action",
                    setState: setFeedbackDetails({ shouldShow: false }),
                    actionType: "edit",
                    messageType:
                      restrictPlanType === brandDeadLine ? messageTypes.deadlinePlan : messageTypes.initiativePlan
                  })
                );
                return;
              }

              setIsOpenEditActionDialog(true);
            }
          },
          {
            label: "Delete Action",
            icon: DeleteIcon,
            accessType: accessTypes.delete,
            enabled: true,
            action: () => {
              if (!isCorp && !matchOwnerWithHltId(selectedMyPlanDetail?.owners)) {
                dispatch(
                  setFeedbackDetails({
                    shouldShow: true,
                    type: "action",
                    displayName: getOwnersList(),
                    setState: setFeedbackDetails({ shouldShow: false }),

                    messageType:
                      selectedMyPlanDetail?.owners.length > 1 ? messageTypes.multipleOwners : messageTypes.singleOwner
                  })
                );
                return;
              } else if (matchPlanType(restrictPlanType) && activeCard?.cardEditable === "N") {
                dispatch(
                  setFeedbackDetails({
                    shouldShow: true,
                    type: "action",
                    setState: setFeedbackDetails({ shouldShow: false }),
                    actionType: "delete",
                    messageType:
                      restrictPlanType === brandDeadLine ? messageTypes.deadlinePlan : messageTypes.initiativePlan
                  })
                );
                return;
              }
              setIsOpenViewActionDialog(false);
              setIsOpenDeleteActionDialog(true);
            }
          }
        ]
      : [
          {
            label: "Copy shareable link",
            icon: CopyLink,
            action: () => {
              const link = planId
                ? REDIRECT_URI + "/plandetails/" + planId + "/" + sectionId + "/" + id + "?isshareable=true"
                : REDIRECT_URI + "/library/search?q=&cardid=" + id;
              navigator.clipboard.writeText(link).then(function () {
                setIsCopyLinkSuccess(true);
              });
              setIsCopyLinkSuccess(false);
            },
            enabled: true
          }
        ];
  };

  const onCopyActionClick = () => {
    setIsOpenCopyActionDialog(true);
    setIsOpenViewActionDialog(false);
  };

  const getDialogTitle = () => {
    return (
      <>
        <Feedback
          form=""
          actionStatus={{ shouldShow: isCopyLinkSuccess, variant: "success", timer: FEEDBACK_TIMER }}
          message="Link Copied"
        />
        <>
          {isTemplatePlan && !isPreview && !isMobileScreen && (
            <Grid item xs={12}>
              <CloseButton
                handleClose={() => {
                  if (planType === planTypes.MulipleAssign) setIsOpenPlanDetaiLDialog(true);
                  handleClose();
                }}
              />
            </Grid>
          )}
          {isEdit || isNew ? (
            <>
              {isMobileScreen && (
                <Grid item xs={12}>
                  <CloseButton
                    handleClose={() => {
                      if (planType === planTypes.MulipleAssign) setIsOpenPlanDetaiLDialog(true);
                      handleClose();
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                {
                  <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
                    {isNew && translate("myPlan.createNewAction")} {isEdit && translate("myPlan.editAction")}
                  </Typography>
                }
                <Input
                  label="Title"
                  placeholder="Add a brief title"
                  value={isNew ? "" : name}
                  register={register}
                  name="name"
                  validations={{ required: true }}
                  error={errors?.name}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Input
                  type="text"
                  numeric
                  label="Duration (in mins)"
                  startAdornment={<TimeClockCircle />}
                  value={isNew ? "" : timeEstimateMinutes}
                  register={register}
                  validations={{
                    required: true,
                    min: 0,
                    max: 9999
                  }}
                  error={errors?.timeEstimateMinutes}
                  name="timeEstimateMinutes"
                />
              </Grid>
            </>
          ) : (
            <>
              {isPreview ? (
                <Grid item xs={12} onClick={() => setShowPreview(false)}>
                  <IconButton sx={{ paddingRight: "0", marginLeft: "-12px" }} onClick={handleClose}>
                    <Backbutton color={tokens.Icon.General.Color} />
                  </IconButton>
                  <ButtonLink
                    text="Back"
                    textVariant={"body2"}
                    underline="none"
                    sx={{
                      fontWeight: "700"
                    }}
                  ></ButtonLink>
                </Grid>
              ) : (
                <>
                  {((planType === planTypes.MyPlans && isMobileScreen) ||
                    planType === planTypes.AllPlans ||
                    planType === planTypes.MulipleAssign) && (
                    <Grid item xs={12} marginTop={isImpersonated && isMobileScreen ? "108px" : ""}>
                      <CloseButton
                        handleClose={() => {
                          if (planType === planTypes.MulipleAssign) setIsOpenPlanDetaiLDialog(true);
                          handleClose();
                        }}
                      />
                    </Grid>
                  )}
                </>
              )}
              <div className="row1">
                <div className="name">
                  <Typography color={tokens.Text.General.PrimaryHeadline} variant="h5" showBold>
                    {name}
                  </Typography>
                </div>
                <div className="actions">
                  <Timing duration={getHoursAndMin(timeEstimateMinutes)} />
                  {!isPreview && <KebabMenu options={getMenuOptions()} />}
                </div>
              </div>

              {!isPreview && (
                <div className="row2">
                  <div>
                    {(activeCard?.deadlineDate || activeCard.milestoneDate) && (
                      <div>
                        <Typography component={"span"} variant={"body2"}>
                          Milestone for deadline:{" "}
                        </Typography>
                        <Typography component={"span"} variant={"body2"} showBold>
                          {getUIFormatDate(activeCard?.deadlineDate || activeCard.milestoneDate)}
                        </Typography>
                      </div>
                    )}
                  </div>
                  {planType === planTypes.AllPlans &&
                    checkCopyActionAccess() &&
                    !location?.state?.redirectFromDashboard && (
                      <Button
                        accessType={accessTypes.update}
                        variant="primary"
                        label="Copy Action to a plan"
                        startIcon={<FileAdd />}
                        onClick={onCopyActionClick}
                      />
                    )}
                </div>
              )}
            </>
          )}
        </>
      </>
    );
  };

  const tabs = useMemo(
    () => getTabDetails(isTemplatePlan ? "TemplatePlan" : planType, sectionId, cardId, planId),
    [isTemplatePlan, planType, sectionId, cardId, planId]
  );

  const getDialogContent = () => {
    if (isPreview) return <Index />;
    if (isEdit || isNew) {
      return (
        <Grid
          container
          spacing={2}
          display="flex"
          flexDirection="column"
          sx={{
            padding: "8px",
            overflow: "auto",
            display: "flex",
            flexDirection: "row"
          }}
        >
          <Grid item xs={12}>
            <Input
              register={register}
              name="summary"
              label="Summary"
              placeholder="Write a short summary"
              isMultiline={true}
              rowsCount={2}
              value={isNew ? "" : activeCard.summary}
            />
          </Grid>

          <Grid item xs={12}>
            <RichText
              label="Body"
              placeholder="Add the details in the body content"
              initialValue={isNew ? "" : activeCard.body}
              handleChangeUpdate={(data) => setBody(data)}
            />
          </Grid>
          <Grid item xs={12} paddingBottom={2}>
            <Resources label="Resources" />
          </Grid>
        </Grid>
      );
    }

    return <UrlTabs isURLDriven={false} defaultTab={isRedirectFromNotification ? 1 : 0} tabs={tabs} enableScroll />;
  };

  const onSubmit = async (data: any) => {
    let tempData = data;
    if (tempData.linkToResources)
      tempData.linkToResources = tempData.linkToResources.filter((item: any) => item?.title !== "");
    tempData = {
      assignee: assigneeVal,
      body,
      ...tempData
    };

    if (isNew) {
      const cards = [];
      cards.push(tempData);
      const cardPayload = { cards: cards };
      const payload = checkTemplatePlanPayload(cardPayload);
      const url = { planId: planId, sectionId: sectionId };
      const res = await dispatch(copyActionAsync({ url, payload }));
      if (res?.payload?.status === responseTypes.created) handleClose();
    } else {
      let payload;
      if (matchPlanType(restrictPlanType) && activeCard?.cardEditable === "N") {
        payload = {
          cards: [
            {
              status: tempData.status,
              assignee: tempData.assignee,
              priority: tempData.priority,
              targetedMetrics: tempData.targetedMetrics,
              dueDate: tempData.dueDate,
              id
            }
          ]
        };
      } else {
        payload = {
          cards: [
            {
              name: activeCard.name,
              id,
              ...tempData
            }
          ]
        };
      }

      const showConfirm =
        data?.status && activeCard.status !== data.status && !activeCard.assignee?.includes(getUserHltId());

      if (showConfirm) {
        setShowConfirmDialog(true);
        setUpdatePayload(payload);
      } else {
        const updateCardPayoad = checkTemplatePlanPayload(payload);
        dispatch(
          updateMyPlanCardDetailAsync({ url: `${planId}/sections/${sectionId}/cards`, payload: updateCardPayoad })
        );
      }
    }
  };

  const handleUpdateAction = () => {
    dispatch(updateMyPlanCardDetailAsync({ url: `${planId}/sections/${sectionId}/cards`, payload: updatePayload }));
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(false);
  };

  return (
    <Dialog
      fullWidth={true}
      fullScreen={isMobileScreen}
      maxWidth={planType === planTypes.AllPlans || planTypes.MulipleAssign ? "md" : "lg"}
      open={open}
      onClose={(e, r) => {
        if (planType !== planTypes.MulipleAssign && r !== "backdropClick") handleClose();
      }}
      PaperProps={{
        sx: {
          overflow: isMobileScreen || isPreview ? "auto" : "hidden"
        }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container className="action_details_dialog">
            <Grid
              sx={{ maxHeight: isMobileScreen ? "none" : "80vh" }}
              xs={12}
              display="flex"
              id="dialogContainer"
              flexDirection={isMobileScreen ? "column" : "row"}
            >
              <Grid
                item
                xs={12}
                md={planType === planTypes.MyPlans && !isTemplatePlan ? 8 : 12}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: isMobileScreen ? "none" : "80vh",
                  overflow: "hidden"
                }}
              >
                <div className="action_details_dialog_title" ref={titleRef} style={{ flexShrink: 0 }}>
                  {getDialogTitle()}
                </div>
                <DialogContent
                  className={`action_details_dialog_content 
                    ${planType === planTypes.MyPlans ? "my_plans" : "all_plans"} 
                    ${isEdit || isNew ? "isNew" : ""}`}
                  id={"actionPlanDialog"}
                  sx={{
                    flex: "1 1 auto",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    padding: "16px 24px"
                  }}
                >
                  {getDialogContent()}
                </DialogContent>
              </Grid>
              {planType === planTypes.MyPlans && !isTemplatePlan && (
                <MyPlanActionDetail
                  setAssigneeVal={setAssigneeVal}
                  assigneeList={assigneeList}
                  handleClose={handleClose}
                  isNew={isNew}
                  handleSubmit={methods.handleSubmit(onSubmit)}
                />
              )}
            </Grid>
            {(isNew || isEdit) && isTemplatePlan && (
              <Grid display="flex" justifyContent="end" width={"100%"}>
                <ActionButtons handleClose={handleClose} handleSubmit={methods.handleSubmit(onSubmit)} />
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>

      <UpdateStatusConfirmDialog open={showConfirmDialog} onConfirm={handleUpdateAction} onClose={handleCloseDialog} />
    </Dialog>
  );
}
